import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useMemo } from 'react';
import { SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import {
    defaultHRM2120_R01HeaderDto,
    HRM2120_R01HeaderDto,
    HRM2120_R01HeaderGridRow,
} from './Dto_HRM2120_R01';
import { useHRM2120_R01Store } from './Store_HRM2120_R01';

/** 사원 목록 참조와 선택 함수를 받는다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2120_R01HeaderGridRow> | null>; // 조회용 사원 그리드
    onSelect: (row: HRM2120_R01HeaderDto) => void; // 클릭한 사원 선택
}

/** 사원 그리드의 컬럼 배치를 저장하는 키이다. */
const COLUMN_STATE_KEY = 'HRM2120_R01-grid-column-state';
/** 저장된 컬럼 배치를 복원할 때 사용할 제목이다. */
const HEADER_NAMES = {
    rowIndex: 'No',
    empNo: '사원번호',
    empNm: '사원명',
    orgNm: '부서',
    positionNm: '직위',
    assignText: '배정',
    baseShiftNm: '기준일 근무조',
    empSts: '재직',
};
/** 달력과 같은 크기와 색상으로 기준일 근무조 이름만 강조한다. */
const SHIFT_BADGE_BASE_CLASS = 'inline-flex min-w-[64px] items-center justify-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-xs font-medium leading-4';
const DAY_BADGE_CLASS = `${SHIFT_BADGE_BASE_CLASS} border-amber-300 bg-amber-100 text-amber-800`;
const NIGHT_BADGE_CLASS = `${SHIFT_BADGE_BASE_CLASS} border-indigo-300 bg-indigo-100 text-indigo-800`;

/** 기준일 근무조와 배정 설명이 포함된 조회 전용 사원 목록이다. */
export const Table_HRM2120_R01 = ({ gridRef, onSelect }: Props) => {
    const {
        HRM2120_R01List,
        selectedHRM2120_R01,
        columnState,
        setColumnState,
        scrollTop,
        setScrollTop,
    } = useHRM2120_R01Store();
    /** 재직상태 이름은 화면 공통코드에서 가져온다. */
    const { empstsCdList } = useCommonCodeStore();
    const {
        getRowId,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
    } = useGridSettingContent<HRM2120_R01HeaderGridRow>({
        gridRef,
        key: 'valueIndex',
        gridData: HRM2120_R01List,
        defaultGridData: defaultHRM2120_R01HeaderDto,
        selectedRow: selectedHRM2120_R01,
    });
    const baseColumnDefs = useMemo<ColDef<HRM2120_R01HeaderGridRow>[]>(() => [
        ...getDefaultRowIndexColumnConfig().map(column => ({
            ...column,
            headerName: 'No',
            width: 56,
        })),
        { headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 100 },
        { headerName: '사원명', colId: 'empNm', field: 'empNm', width: 88 },
        { headerName: '부서', colId: 'orgNm', field: 'orgNm', width: 96 },
        { headerName: '직위', colId: 'positionNm', field: 'positionNm', width: 60 },
        { headerName: '배정', colId: 'assignText', field: 'assignText', width: 110 },
        {
            headerName: '기준일 근무조',
            colId: 'baseShiftNm',
            field: 'baseShiftNm',
            width: 100,
            cellStyle: { textAlign: 'center', justifyContent: 'center' },
            cellRenderer: (params: ICellRendererParams<HRM2120_R01HeaderGridRow>) => {
                const shiftName = params.data?.baseShiftNm;
                if (!shiftName) return null;
                const badgeClass = params.data?.isBaseShiftNight ? NIGHT_BADGE_CLASS : DAY_BADGE_CLASS;
                return <span className={badgeClass}>{shiftName}</span>;
            },
        },
        {
            headerName: '재직',
            colId: 'empSts',
            field: 'empSts',
            width: 62,
            // 원본 상태 코드가 숫자여도 화면에 표시하는 상태명은 왼쪽에 배치한다.
            cellStyle: { textAlign: 'left', justifyContent: 'flex-start' },
            valueFormatter: params => empstsCdList.find(code => code.minorCd === String(params.value))?.codeNm
                || String(params.value ?? ''),
        },
    ], [empstsCdList]);
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    /** 저장된 컬럼 순서·너비를 공통 컬럼 정의 함수로 복원한다. */
    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM2120_R01HeaderGridRow>({
            baseColumnDefs,
            columnState,
            headerNames: HEADER_NAMES,
        });
    }, [baseColumnDefs, columnState]);
    /** 조회한 사원 건수를 제목이 아닌 그리드 하단의 사원번호 칸에 표시한다. */
    const pinnedBottomRow = useMemo(() => [{
        empNo: `${HRM2120_R01List.length.toLocaleString()}건`,
    }], [HRM2120_R01List.length]);
    const selectedIds = selectedHRM2120_R01 ? [selectedHRM2120_R01.valueIndex] : [];
    /** 공통 셀 규칙으로 하단 고정 행의 건수를 가운데 정렬한다. */
    const cellCssDefaultColDef = {
        ...defaultColDef,
        editable: false,
        cellClassRules: createCellClassRules<HRM2120_R01HeaderGridRow>({
            idKey: 'valueIndex',
            selectedList: selectedIds,
            deletedList: [],
            disabledInsertedColumns: [],
            disabledColumns: [],
            pointerInsertedColumns: [],
            pointerColumns: [],
        }),
    };

    return (
        <section className="flex flex-col h-full min-h-0">
            <GridContextWrapper<HRM2120_R01HeaderGridRow>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                defaultRowData={defaultHRM2120_R01HeaderDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}>
                {/* 공통 제목 오른쪽에 기존 기준일자의 근무조 안내를 유지한다. */}
                <SmallTitleWithSubSpan title="사원 목록" subTitle="기준일자의 근무조" />
                <Grid_Excel<HRM2120_R01HeaderGridRow>
                    ref={gridRef}
                    rowData={paddedGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={createRowClassRules<HRM2120_R01HeaderGridRow>({
                        idKey: 'valueIndex',
                        selectedList: selectedIds,
                        deletedList: [],
                    })}
                    onRowClicked={event => {
                        if (!event.node.rowPinned && event.data?.empNo) onSelect(event.data);
                    }}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop} />
            </GridContextWrapper>
        </section>
    );
};
